import React from "react";

type TopPlan = {
    title: string;
    price: string;
    subPrice?: string;
    description: string;
    promo: string;
    gradientClass: string;
    ribbon?: boolean;
};

type BottomPlan = {
    title: string;
    prices: Array<{ value: string; note?: string }>;
    description: string;
    promo?: string;
    buttonVariant?: "black" | "gray";
};

const topPlans: TopPlan[] = [
    {
        title: "MONTHLY UNLIMITED",
        price: "$159",
        subPrice: "+GST based on annual membership",
        description: "Unlimited access to all group classes to\nfor maximum wellness",
        promo: "Black Friday 😇 Save $99 on enrollment",
        gradientClass: "bg-gradient-to-br from-[#2E5984] to-[#4A729A]",
        ribbon: true,
    },
    {
        title: "ANNUAL UNLIMITED\nPREPAID",
        price: "$1,499",
        subPrice: "save 25%\n+GST",
        description:
            "1-year Prepaid Unlimited access to all of our\nyoga, fusion and fitness classes at a discount\n–our best value!",
        promo: "Black Friday 🖤 Save $99 on enrollment",
        gradientClass: "bg-gradient-to-br from-[#54A6B9] to-[#58B099]",
        ribbon: true,
    },
];

const bottomPlans: BottomPlan[] = [
    {
        title: "1 MONTH UNLIMITED",
        prices: [{ value: "$179" }],
        description:
            "Get 1 month unlimited access to all our yoga,\nfusion, and fitness classes",
        buttonVariant: "black",
    },
    {
        title: "3 MONTH UNLIMITED",
        prices: [{ value: "$499" }],
        description:
            "Get 3 months unlimited access to all our\nyoga, fusion, and fitness classes",
        promo: "Black Friday 🖤 Save $50 on package",
        buttonVariant: "gray",
    },
    {
        title: "PUNCH PASSES",
        prices: [
            { value: "$199", note: "10 class passes" },
            { value: "$379", note: "20 class passes" },
        ],
        description: "Get access to all our yoga, fusion, and fitness\nclasses",
        promo: "Black Friday 🖤 Save $20 on package",
        buttonVariant: "black",
    },
];

function BestValueRibbon() {
    return (
        <div className="pointer-events-none absolute right-[-30px] top-[-26px] h-[110px] w-[120px] rotate-45">
            <div className="absolute left-0 top-[44px] h-[21px] w-full bg-white" />
            <div
                className="absolute left-0 top-[44px] flex h-[21px] w-full items-center justify-center"
                style={{
                    WebkitTextFillColor: "transparent",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    backgroundImage:
                        "linear-gradient(90deg, #141414 0%, #025893 73.56%)",
                }}
            >
        <span className="font-helvetica text-[8.6px] font-bold uppercase leading-[12px]">
          BEST VALUE
        </span>
            </div>
        </div>
    );
}

function PillButton({
                        children,
                        variant = "white",
                    }: {
    children: React.ReactNode;
    variant?: "white" | "black" | "gray";
}) {
    const cls =
        variant === "white"
            ? "bg-white text-[#141414] border-2 border-white"
            : variant === "gray"
                ? "bg-[#4B5563] text-white border-2 border-[#4B5563]"
                : "bg-black text-white border-2 border-black";

    return (
        <button
            type="button"
            className={[
                "mx-auto inline-flex items-center justify-center rounded-full px-6",
                "h-[38px] text-[12px] font-bold uppercase tracking-wide",
                "font-helvetica",
                cls,
            ].join(" ")}
        >
            {children}
        </button>
    );
}

export default function MembershipSection() {
    return (
        <section className="bg-white">
            <div className="mx-auto w-full max-w-[1200px] px-4 py-10 sm:py-14">
                <h2 className="text-center font-helvetica text-[40px] font-medium leading-[44px] text-black sm:text-[52px] sm:leading-[56px]">
                    Memberships
                </h2>

                {/* Top row */}
                <div className="mt-8 flex justify-center sm:mt-10">
                    <div className="inline-flex w-full flex-col items-center gap-4 sm:w-auto sm:flex-row sm:gap-6">
                        {topPlans.map((p) => (
                            <div
                                key={p.title}
                                className={[
                                    "relative overflow-hidden rounded-[30px] text-white",
                                    p.gradientClass,
                                    // fixed desktop size, mobile = 100%
                                    "w-full sm:w-[380px]",
                                    // make equal height and allow pushing button to bottom
                                    "flex flex-col",
                                    "h-auto sm:h-[331px]",
                                ].join(" ")}
                            >
                                {p.ribbon ? <BestValueRibbon /> : null}

                                {/* content wrapper full height */}
                                <div className="flex h-full flex-col px-6 pb-6 pt-6 sm:px-7 sm:pb-7">
                                    <div className="font-helvetica text-[23.4px] font-bold uppercase leading-[31px]">
                                        {p.title}
                                    </div>

                                    <div className="mt-1 flex items-end gap-2">
                                        <div className="font-helvetica text-[24px] font-normal leading-[30px]">
                                            {p.price}
                                        </div>
                                        {p.subPrice ? (
                                            <div className="whitespace-pre-line font-helvetica text-[11.3px] font-normal leading-[18px] opacity-95">
                                                {p.subPrice}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="mt-5 whitespace-pre-line font-helvetica text-[15.1px] font-normal leading-[22px]">
                                        {p.description}
                                    </div>

                                    {/*<div className="mt-4 font-helvetica text-[14.6px] font-normal leading-[22px]">*/}
                                    {/*    {p.promo}*/}
                                    {/*</div>*/}

                                    {/* push button to bottom so both cards align */}
                                    <div className="mt-auto flex pt-6">
                                        <PillButton variant="white">Join Now</PillButton>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom row */}
                <div className="mt-8 grid grid-cols-1 gap-4 sm:mt-10 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                    {bottomPlans.map((p) => (
                        <div
                            key={p.title}
                            className="rounded-[30px] bg-white shadow-[5px_5px_30px_rgba(0,0,0,0.2)]"
                        >
                            <div className="px-6 pb-6 pt-6 sm:px-7 sm:pb-7">
                                <div className="font-helvetica text-[23.4px] font-bold uppercase leading-[31px] text-[#0C0C0C]">
                                    {p.title}
                                </div>

                                <div className="mt-2 space-y-1">
                                    {p.prices.map((pr) => (
                                        <div key={pr.value} className="flex items-center gap-3">
                                            <div className="font-helvetica text-[24px] font-normal leading-[30px] text-[#0C0C0C]">
                                                {pr.value}
                                            </div>
                                            {pr.note ? (
                                                <div className="font-helvetica text-[11.3px] font-normal leading-[18px] text-[#0C0C0C]">
                                                    {pr.note}
                                                </div>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-5 whitespace-pre-line font-helvetica text-[15.1px] font-normal leading-[22px] text-black">
                                    {p.description}
                                </div>

                                {/*{p.promo ? (*/}
                                {/*    <div className="mt-4 font-helvetica text-[14.6px] font-normal leading-[22px] text-black">*/}
                                {/*        {p.promo}*/}
                                {/*    </div>*/}
                                {/*) : (*/}
                                {/*    <div className="mt-4 h-[22px]" />*/}
                                {/*)}*/}

                                <div className="mt-10 flex pb-2">
                                    <PillButton
                                        variant={p.buttonVariant === "gray" ? "gray" : "black"}
                                    >
                                        Buy Now
                                    </PillButton>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
